const MAX_RECURSION_DEPTH = 5

export function hasAValue(value: unknown): boolean {
  return ![null, undefined].includes(value as null | undefined)
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export function serializeObject(originalValue: any): any {
  if (typeof originalValue !== 'object' || originalValue === null) {
    return originalValue
  }

  const replacedValue = Array.isArray(originalValue) ? [] : {}
  const currentRecursionDepth = 0

  return destroyCircularRefs(originalValue, replacedValue, currentRecursionDepth)
}

function destroyCircularRefs(
  originalValue: any,
  replacedValue: any,
  currentRecursionDepth: number,
  circularCache: WeakSet<any> = new WeakSet([originalValue]),
): any {
  circularCache = circularCache ?? new WeakSet([originalValue])

  const commonErrorProperties = ['name', 'message', 'stack']
  const ownProps = Object.getOwnPropertyNames(originalValue)

  ownProps.forEach((propName) => {
    const currentChildValue = originalValue[propName]

    if (typeof currentChildValue === 'object' && currentChildValue !== null) {
      // Stop serializing after reaching a certain depth to prevent extraneous payload sizes
      if (currentRecursionDepth >= MAX_RECURSION_DEPTH) {
        replacedValue[propName] = '[Redacted]'
        return
      }

      if (circularCache.has(currentChildValue)) {
        replacedValue[propName] = '[Circular]'
        return
      }

      circularCache.add(currentChildValue)

      const replacedChildValue = Array.isArray(currentChildValue) ? [] : {}

      replacedValue[propName] = destroyCircularRefs(
        currentChildValue,
        replacedChildValue,
        currentRecursionDepth + 1,
        circularCache,
      )

      return
    } else {
      replacedValue[propName] = currentChildValue
    }
  })

  // Iterate common non-enumerable props found inside of error objects
  for (const propName of commonErrorProperties) {
    const valueFromError = originalValue[propName]

    if (typeof valueFromError === 'string') {
      replacedValue[propName] = valueFromError
    }
  }

  return replacedValue
}

export function removeEmptyValues<T extends Record<string, any>>(rawObject: T): Partial<T> {
  if (!rawObject) {
    return {}
  }

  return Object.keys(rawObject).reduce<Partial<T>>((cleanObject, key) => {
    const value = rawObject[key]

    if (!hasAValue(value)) {
      return cleanObject
    }

    cleanObject[key as keyof T] = value

    return cleanObject
  }, {} satisfies Partial<T>)
}

export function recursivelyRemoveEmptyValues<T extends Record<string, any>>(
  rawObject: T,
): Partial<T> {
  if (!rawObject) {
    return {}
  }

  if (typeof rawObject !== 'object') {
    return rawObject
  }

  return Object.entries(rawObject)
    .filter(([, value]) => hasAValue(value))
    .reduce<Partial<T>>((cleanObject, [key, value]) => {
      if (Array.isArray(value)) {
        cleanObject[key as keyof T] = value.map((item) => recursivelyRemoveEmptyValues(item)) as any
      } else if (typeof value === 'object') {
        cleanObject[key as keyof T] = recursivelyRemoveEmptyValues(
          value as Record<string, any>,
        ) as T[keyof T]
      } else {
        cleanObject[key as keyof T] = value
      }
      return cleanObject
    }, {} satisfies Partial<T>)
}
